import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import Alert from 'components/global/Alert';
import Authorization from 'components/global/Authorization';
import Loading from 'components/global/Loading';

import useMediaQuery from 'hooks/useMediaQuery';
import useUpdateEffect from 'hooks/useUpdateEffect';

import AddPaymentMethodModal from 'pages/Settings/Payment/ManageSubscription/AddPaymentMethod/AddPaymentMethodModal';
import ShipmentInfoBar from 'pages/Shipments/ActionForms/CreateShipment/Components/ShipmentInfoBar';

import { getClientCards } from 'stores/userSlice';

import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';

import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';

import ShippingOptions from '../Shipments/ActionForms/CreateShipment/ShippingOptions';
import PackageSelection from './PackageSelection';
import * as S from './PriceCalculator.styled';
import { CreateShipmentBottom } from './QuickShipments/QuickShipment.styled';
import ShippingInformation from './ShippingInformation/ShippingInformation';

export const ShippingDetailsAndOptionsTab = ({
	nextStep,
	isContinuesAction,
	setIsContinuesAction,
	disabledSelectOrder = false,
	setHasOptionalPickup,
	isInternationalToQatar,
	setIsInternationalToQatar,
	dcCompanyData,
	setDcCompanyData,
	showBackButton,
	setNoticeData,
	isAggregatorBulletDelivery,
	setIsAggregatorBulletDelivery,
	isImportOrder,
	setIsLockerDelivery,
	isQuickShipment,
	prevStep,
	resetStep
}) => {
	const isTablet = useMediaQuery(Breakpoints.tablet);
	const formMethods = useFormContext();
	const dispatch = useDispatch();
	const { watch, setValue, control } = formMethods;
	const editMode = watch('editMode');
	const [tableRef, setTableRef] = useState(null);
	const [shippingInformationCardRef, setShippingInformationCardRef] = useState(null);
	const [volumetricWeight, setVolumetricWeight] = useState(null);
	const [clientCRDocumentStatus, setClientCRDocumentStatus] = useState(null);

	const { planName: currentPackage, clientCards } = useSelector((state) => state?.user);
	const noSavedCard = clientCards ? clientCards.length === 0 : true;
	const orderID = watch('orderID');
	const isInternationalOrder = watch('originAddress.countryName') !== watch('destinationAddress.countryName');

	const [selectedPackage, setSelectedPackage] = useState(currentPackage);
	const navigate = useNavigate();
	const reloadPrices = useWatch({ control, name: 'reloadPrices' });

	useUpdateEffect(() => {
		setValue('editMode', true);
		setValue('reloadPrices', true);
	}, [currentPackage]);

	useEffect(() => {
		setIsAggregatorBulletDelivery(false);
		setIsLockerDelivery(false);
	}, []);

	useEffect(() => {
		dispatch(
			getClientCards({
				isCvvRequire: false
			})
		);
	}, []);

	return (
		<S.ShippingDetailsTabContainer>
			{orderID && isInternationalOrder && noSavedCard && !reloadPrices && (
				<Alert
					variant="error"
					showIcon
					className="mb-2"
					content="priceCalculator.noSavedCardForInternationalOrder"
					ctaButtonLabel="addPaymentMethod"
					onCtaButtonClick={() => {
						NiceModal.show(AddPaymentMethodModal, {
							customAlertText: 'addPaymentMethod.noSavedCardForInternationalOrder'
						});
					}}
				/>
			)}
			{(editMode || !dcCompanyData) && (
				<>
					<S.ShippingInformationCard
						className={reloadPrices && isQuickShipment ? 'doNotDisplay' : ''}
						ref={setShippingInformationCardRef}
						fullWidth
						rounded>
						<ShippingInformation
							setDcCompanyData={setDcCompanyData}
							isContinuesAction={isContinuesAction}
							setIsContinuesAction={setIsContinuesAction}
							disabledSelectOrder={disabledSelectOrder}
							shippingInformationCardRef={shippingInformationCardRef}
							tableRef={tableRef}
							setIsInternationalToQatar={setIsInternationalToQatar}
							showBackButton={showBackButton}
							editMode={editMode}
							setVolumetricWeight={setVolumetricWeight}
							setClientCRDocumentStatus={setClientCRDocumentStatus}
							isImportOrder={isImportOrder}
							isQuickShipment={isQuickShipment}
						/>
					</S.ShippingInformationCard>
					<Authorization path={PATHS.SAVINGS_CALCULATOR}>
						<Alert
							header="savingsCalculatorAlertHeader"
							content="savingsCalculatorAlertContent"
							ctaButtonLabel="goToSavingsCalculator"
							showIcon
							iconUrl="https://storage.googleapis.com/tryoto-public/public/assets/icons/Savings-Calculator/savings-calculator-icon.svg"
							onCtaButtonClick={() => navigate(PATHS.SAVINGS_CALCULATOR)}
							style={{
								backgroundColor: 'var(--table-active-color)'
							}}
						/>
					</Authorization>
				</>
			)}
			{isQuickShipment && reloadPrices && !dcCompanyData && <Loading />}

			{dcCompanyData && (
				<>
					{!editMode && (
						<ShipmentInfoBar volumetricWeight={volumetricWeight} isQuickShipment={isQuickShipment} />
					)}
					{((isTablet && !editMode) || !isTablet) && (
						<>
							{(currentPackage === 'FreePackage' || currentPackage === 'StarterPackage') && (
								<Authorization path={PATHS.OTO_MODULES}>
									<PackageSelection
										selectedPackage={selectedPackage}
										setSelectedPackage={setSelectedPackage}
									/>
								</Authorization>
							)}
							<ShippingOptions
								dcCompanyData={dcCompanyData}
								nextStep={nextStep}
								setTableRef={setTableRef}
								isInternationalToQatar={isInternationalToQatar}
								setHasOptionalPickup={setHasOptionalPickup}
								setNoticeData={setNoticeData}
								isAggregatorBulletDelivery={isAggregatorBulletDelivery}
								setIsAggregatorBulletDelivery={setIsAggregatorBulletDelivery}
								setIsLockerDelivery={setIsLockerDelivery}
								clientCRDocumentStatus={clientCRDocumentStatus}
								isQuickShipment={isQuickShipment}
								resetStep={resetStep}
								setDcCompanyData={setDcCompanyData}
								selectedPackage={
									currentPackage !== selectedPackage &&
									(currentPackage === 'FreePackage' || currentPackage === 'StarterPackage')
										? selectedPackage
										: undefined
								}
								addPaymentMethodRequired={isInternationalOrder && noSavedCard}
							/>
							{isQuickShipment && (
								<CreateShipmentBottom className="removeMargin">
									<Button
										label="cancel"
										onClick={() => {
											navigate(-1);
										}}
										variant="blackOutline"
										css={css`
											text-decoration: underline;
											border: none !important;
										`}
									/>
									<Button
										icon={faChevronLeft}
										label="previous"
										variant="outline"
										onClick={() => {
											setDcCompanyData(null);
											prevStep();
										}}
									/>
								</CreateShipmentBottom>
							)}
						</>
					)}
				</>
			)}
		</S.ShippingDetailsTabContainer>
	);
};

export default ShippingDetailsAndOptionsTab;
